// Cómo se llama una familia en pantalla.
//
// Toda ficha pertenece a una familia, incluida la del jugador que responde por sí
// mismo. Pero llamarle "Familia Pérez" a alguien que se paga solo se lee mal: ahí no
// hay familia, hay una persona.
//
// La condición es de IDENTIDAD, no de edad: el jugador es su propio responsable
// cuando él mismo figura como tutor. La edad no decide nada -- lo que decide es
// quién está puesto como responsable.
//
// Se DERIVA en vez de guardarse en una columna, por dos razones: aplica igual a las
// familias que ya existían (nadie tiene que ir a marcarlas) y se corrige sola si
// mañana entra un hermano o cambia el responsable.

import { isMinor } from './age'

// EL RESPONSABLE SE LEE DEL VINCULO, no de familias.tutor_* (columnas retiradas en la
// Fase 3). Es la persona con rol 'responsable' y es_principal. Sin vinculo, null: no se
// inventa desde ningun texto.
export const EMBED_RESPONSABLE =
  'familia_personas(rol, es_principal, personas(id, nombres, apellidos, nombre_completo, telefono, email))'

export const responsableDeFamilia = (familia) => {
  const v = (familia?.familia_personas || []).find((x) => x?.rol === 'responsable' && x?.es_principal && x?.personas)
  if (!v) return null
  const p = v.personas
  return {
    persona_id: p.id, nombres: p.nombres, apellidos: p.apellidos,
    nombre_completo: p.nombre_completo, telefono: p.telefono, email: p.email,
  }
}

export const conResponsable = (familia) => (familia ? { ...familia, responsable: responsableDeFamilia(familia) } : null)

const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()

// ¿Es una sola persona que responde por sí misma?
//
// Con hermanos SÍ hay familia, aunque el responsable sea uno de ellos. Y sin tutor
// capturado no se sabe quién responde, así que se deja "Familia": afirmar que el
// jugador se paga solo sería inventar un dato que nadie capturó.
export const esAutoResponsable = (familia) => {
  const jugadores = (familia?.jugadores || []).filter((j) => !j?.deleted)
  if (jugadores.length !== 1) return false

  const tutor = norm(familia?.responsable?.nombre_completo)
  if (!tutor) return false

  return tutor === norm(jugadores[0]?.nombre_completo)
}

// El nombre a mostrar es EL DE QUIEN RESPONDE, no "Familia <apellido>".
//
// POR QUE: el apellido no identifica. En producción hay SIETE "Familia González"
// distintas, y aparte una "Familia GONZÁLEZ" que el sistema cuenta como otra por las
// mayúsculas. En total, 65 de 190 familias (34%) comparten nombre con otra: elegir
// entre ellas en una lista es adivinar.
//
// El responsable sí identifica -- "Ignacio González" y "Perla Yahaira Hernández
// González" no se confunden -- y desde que hay UN responsable por familia (el modelo
// de `personas`), es un dato que siempre está o debería estar.
//
// Se DERIVA en vez de guardarse, y eso es la mitad del arreglo: el nombre guardado
// se calculó una vez con una heurística que tomaba el apellido materno, y ahí quedó
// fosilizado ("Familia Gándara" para los Ruiz Gándara). Un nombre derivado se
// corrige solo cuando cambia el responsable, y no hay dos fuentes de verdad.
//
// La palabra "Familia" queda para las que no tienen responsable capturado -- ahí no
// hay de dónde sacar un nombre, y salen listadas en Fichas por completar.
export const nombreFamilia = (familia) => {
  const jugadores = (familia?.jugadores || []).filter((j) => !j?.deleted)

  // Quien responde por sí mismo cae aquí igual, y con su propio nombre: el responsable
  // ES él. No hace falta tratarlo aparte.
  const responsable = String(familia?.responsable?.nombre_completo || '').trim()
  if (responsable) return responsable

  const propio = String(familia?.nombre || '').trim()
  if (propio) return propio
  return jugadores[0]?.nombre_completo || 'Sin nombre'
}

// Quién responde por esta familia. En el caso de auto-responsable no tiene sentido
// repetir el nombre en dos columnas: se devuelve vacío para que la UI lo marque.
export const responsableFamilia = (familia) =>
  esAutoResponsable(familia) ? '' : String(familia?.responsable?.nombre_completo || '').trim()

// QUIEN RESPONDE POR ESTE JUGADOR cuando hay que crearle su familia desde cero.
//
// Lo usan los dos caminos que crean una familia sin pasar por el alta: Estado de
// cuenta > "Compartir" sobre un jugador sin familia, y Fichas por completar. Ese camino
// creaba la familia vacia y leia `jugador.telefono`, un campo que la ficha no tiene.
//
// Manda lo que la ficha ya dice (`contacto`, que es lo que capturo el alta o la
// importacion). Si no dice nada y el jugador es mayor de edad, responde por si mismo con
// su telefono. Si es menor sin responsable, no hay a quien poner y NO se inventa: la
// familia nace sin responsable y sale en Fichas por completar, que es lo correcto.
// Sin fecha de nacimiento no se asume mayor de edad.
//
// `asOf` es inyectable para que la prueba no dependa del dia en que corre.
export const responsableParaCuenta = (j = {}, asOf = new Date()) => {
  const limpio = (t) => String(t || '').trim()
  const contacto = limpio(j.contacto)
  if (contacto) {
    return {
      nombre: contacto,
      telefono: limpio(j.telefono_contacto),
      email: limpio(j.email_tutor),
      esElJugador: norm(contacto) === norm(j.nombre_completo),
    }
  }
  if (isMinor(j.fecha_nacimiento, asOf) === false) {
    return { nombre: limpio(j.nombre_completo), telefono: limpio(j.telefono_jugador), email: '', esElJugador: true }
  }
  return null
}
